import {
  Component,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
  computed,
  ViewEncapsulation
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  FileUploadModule,
  FileSelectEvent,
  FileRemoveEvent
} from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';

/**
 * Evento emitido al seleccionar un archivo válido
 */
export interface FileSelectedEvent {
  /** El archivo seleccionado */
  file: File;
  /** Nombre del archivo */
  fileName: string;
  /** Tamaño en bytes */
  fileSize: number;
}

/**
 * FileUploaderComponent
 *
 * Componente simple para selección de archivos Excel.
 * NO realiza parseo ni validación del contenido - eso lo hace el backend.
 *
 * Solo valida:
 * - Extensión del archivo (.xls, .xlsx)
 * - Tamaño máximo del archivo
 *
 * ## Uso:
 * ```html
 * <app-file-uploader
 *   [templateUrl]="'/api/plantillas/creacion'"
 *   [templateFileName]="'Plantilla_CreacionCita.xlsx'"
 *   [maxFileSize]="10 * 1024 * 1024"
 *   (onFileSelected)="handleFile($event)"
 *   (onFileRemoved)="handleRemoved()">
 * </app-file-uploader>
 * ```
 *
 * @author GIROP Migration
 * @since 2.0
 */
@Component({
  selector: 'app-file-uploader',
  standalone: true,
  imports: [
    CommonModule,
    FileUploadModule,
    ButtonModule,
    MessageModule,
    TooltipModule
  ],
  template: `
    <div class="file-uploader">
      <!-- Input file oculto para selección manual -->
      <input
        type="file"
        #hiddenFileInput
        [accept]="acceptedExtensions"
        (change)="onNativeFileSelect($event)"
        class="hidden"
      />

      <!-- Zona de carga -->
      <div class="upload-zone">
        <p-fileUpload
          #fileUpload
          mode="advanced"
          [accept]="acceptedExtensions"
          [maxFileSize]="maxFileSize()"
          [showUploadButton]="false"
          [showCancelButton]="false"
          [auto]="true"
          [customUpload]="true"
          (onSelect)="onFileSelect($event)"
          (onRemove)="onFileRemove($event)"
          (uploadHandler)="onUploadHandler($event)"
          chooseLabel="Seleccionar archivo"
          chooseIcon="pi pi-file-excel"
          styleClass="w-full"
        >
          <ng-template pTemplate="header">
            <div
              class="flex flex-wrap items-center justify-between gap-2 w-full"
            >
              <span class="text-sm text-gray-600 min-w-0">
                Formatos aceptados: XLS, XLSX (máx. {{ maxFileSizeMB() }}MB)
              </span>
              @if (templateUrl()) {
                <button
                  type="button"
                  pButton
                  class="p-button-outlined"
                  severity="info"
                  icon="pi pi-download"
                  label="Descargar plantilla"
                  size="small"
                  styleClass="min-h-[44px]"
                  (click)="downloadTemplate(); $event.stopPropagation()"
                  pTooltip="Descargar plantilla Excel"
                ></button>
              }
            </div>
          </ng-template>

          <ng-template pTemplate="content">
            @if (!hasFile()) {
              <div
                class="flex flex-col items-center justify-center py-8 text-gray-500 cursor-pointer transition-colors rounded-lg"
                (click)="triggerFileInput(hiddenFileInput)"
              >
                <i class="pi pi-file-excel text-5xl mb-4 text-green-600"></i>
                <p class="mb-2 font-medium">Arrastra un archivo Excel aquí</p>
                <p class="text-sm">o haz clic para seleccionar</p>
                <p-button
                  label="Seleccionar archivo"
                  icon="pi pi-folder-open"
                  severity="primary"
                  [outlined]="true"
                  size="small"
                  styleClass="mt-4 min-h-[44px]"
                  (onClick)="
                    triggerFileInput(hiddenFileInput); $event.stopPropagation()
                  "
                >
                </p-button>
              </div>
            }
          </ng-template>

          <ng-template pTemplate="file" let-file>
            <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <i class="pi pi-file-excel text-3xl text-green-600"></i>
              <div class="flex-1 min-w-0">
                <p class="font-medium text-gray-800">{{ file.name }}</p>
                <p class="text-sm text-gray-500">
                  {{ (file.size / 1024).toFixed(1) }} KB
                </p>
              </div>
              <p-button
                icon="pi pi-times"
                severity="danger"
                [rounded]="true"
                [text]="true"
                styleClass="min-w-[44px] min-h-[44px]"
                (onClick)="clear(); fileUpload.clear()"
              >
              </p-button>
            </div>
          </ng-template>
        </p-fileUpload>
      </div>

      <!-- Mensaje de error -->
      @if (errorMessage()) {
        <p-message
          severity="error"
          [text]="errorMessage()!"
          styleClass="mt-4 w-full"
        >
        </p-message>
      }

      <!-- Mensaje de archivo seleccionado -->
      @if (hasFile() && !errorMessage()) {
        <p-message
          severity="info"
          [text]="'Archivo listo para enviar: ' + selectedFile()!.name"
          styleClass="mt-4 w-full"
        >
        </p-message>
      }
    </div>
  `,
  styles: [
    `
      .file-uploader {
        .p-fileupload-content {
          padding: 0;
        }
        .p-fileupload-buttonbar {
          display: none;
        }
      }
    `
  ],
  encapsulation: ViewEncapsulation.None
})
export class FileUploaderComponent {
  private readonly http = inject(HttpClient);

  // ==================== INPUTS ====================

  /** URL de la plantilla para descargar */
  templateUrl = input<string | null>(null);

  /** Nombre del archivo de plantilla para descargar */
  templateFileName = input<string>('plantilla.xlsx');

  /** Tamaño máximo del archivo en bytes (default 10MB) */
  maxFileSize = input<number>(10 * 1024 * 1024);

  // ==================== OUTPUTS ====================

  /** Emite cuando se selecciona un archivo válido */
  @Output() onFileSelected = new EventEmitter<FileSelectedEvent>();

  /** Emite cuando se remueve el archivo */
  @Output() onFileRemoved = new EventEmitter<void>();

  /** Emite cuando hay un error */
  @Output() onError = new EventEmitter<string>();

  // ==================== SIGNALS ====================

  /** Archivo seleccionado */
  selectedFile = signal<File | null>(null);

  /** Error actual */
  errorMessage = signal<string | null>(null);

  // ==================== COMPUTED ====================

  /** Indica si hay archivo seleccionado */
  hasFile = computed(() => this.selectedFile() !== null);

  /** Tamaño máximo en MB */
  maxFileSizeMB = computed(() => this.maxFileSize() / 1024 / 1024);

  /** Extensiones aceptadas */
  acceptedExtensions = '.xls,.xlsx';

  // ==================== MÉTODOS ====================

  /**
   * Dispara el input file nativo para selección manual
   */
  triggerFileInput(inputElement: HTMLInputElement): void {
    inputElement.click();
  }

  /**
   * Maneja la selección desde el input file nativo
   */
  onNativeFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFile(input.files[0]);
      // Reset input para permitir seleccionar el mismo archivo
      input.value = '';
    }
  }

  /**
   * Handler para customUpload (requerido por p-fileUpload)
   */
  onUploadHandler(_event: { files: File[] }): void {
    // No hacemos nada aquí
  }

  /**
   * Maneja la selección del archivo desde p-fileUpload
   */
  onFileSelect(event: FileSelectEvent): void {
    if (event.files[0]) {
      this.processFile(event.files[0]);
    }
  }

  /**
   * Maneja la eliminación del archivo
   */
  onFileRemove(_event: FileRemoveEvent): void {
    this.clear();
    this.onFileRemoved.emit();
  }

  /**
   * Procesa el archivo seleccionado (solo validación básica)
   */
  private processFile(file: File): void {
    this.errorMessage.set(null);

    // Validar extensión
    const ext = file.name.toLowerCase().split('.').pop();
    if (ext !== 'xls' && ext !== 'xlsx') {
      const msg = 'Solo se permiten archivos Excel (.xls o .xlsx)';
      this.errorMessage.set(msg);
      this.onError.emit(msg);
      return;
    }

    // Validar tamaño
    if (file.size > this.maxFileSize()) {
      const msg = `El archivo excede el tamaño máximo de ${this.maxFileSizeMB()}MB`;
      this.errorMessage.set(msg);
      this.onError.emit(msg);
      return;
    }

    // Archivo válido
    this.selectedFile.set(file);
    this.onFileSelected.emit({
      file,
      fileName: file.name,
      fileSize: file.size
    });
  }

  /**
   * Limpia el estado del componente
   */
  clear(): void {
    this.selectedFile.set(null);
    this.errorMessage.set(null);
  }

  /**
   * Retorna el archivo seleccionado (para uso externo)
   */
  getFile(): File | null {
    return this.selectedFile();
  }

  /**
   * Descarga la plantilla
   */
  downloadTemplate(): void {
    const url = this.templateUrl();
    if (!url) return;

    const fileName = this.templateFileName();

    this.http.get(url, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      },
      error: (err) => {
        console.error('Error descargando plantilla:', err);
        this.errorMessage.set('Error al descargar la plantilla');
      }
    });
  }
}
